import {
  // Navigation & UI
  Home,
  Target,
  Zap,
  BookOpen,
  TrendingUp,
  Settings,
  ChevronRight,
  ChevronLeft,
  X,
  Check,
  Plus,
  Minus,
  Menu,

  // Progress & Achievement
  Trophy,
  Award,
  Medal,
  Star,
  Crown,
  Flame,
  Sparkles,

  // Stress & Wellness
  Heart,
  HeartPulse,
  Brain,
  Wind,
  Droplets,
  Activity,
  Timer,
  Clock,

  // Actions & Concepts
  RefreshCw,
  RotateCcw,
  Play,
  Pause,
  Square,
  Volume2,
  VolumeX,
  Shield,
  Lightbulb,
  Compass,
  Map,

  // Calendar & Time
  Calendar,
  CalendarDays,
  CalendarCheck,

  // Communication
  MessageCircle,
  Send,
  Bell,
  BellRing,

  // Data & Analytics
  BarChart3,
  LineChart,
  PieChart,
  ArrowUp,
  ArrowDown,
  TrendingDown,

  // User & Profile
  User,
  Users,
  GraduationCap,

  // Misc
  Pencil,
  FileText,
  BookMarked,
  Dumbbell,
  Coffee,
  Moon,
  Sun,
  Sunrise,

  type LucideIcon,
} from 'lucide-react';

// Semantic icon mapping for the app
export const Icons = {
  // Navigation
  home: Home,
  goals: Target,
  stress: Zap,
  learn: BookOpen,
  progress: TrendingUp,
  settings: Settings,

  // UI Controls
  close: X,
  check: Check,
  plus: Plus,
  minus: Minus,
  menu: Menu,
  next: ChevronRight,
  prev: ChevronLeft,

  // Achievement categories
  achievement: Trophy,
  award: Award,
  medal: Medal,
  star: Star,
  crown: Crown,
  streak: Flame,
  sparkle: Sparkles,

  // Stress & Body
  heart: Heart,
  heartbeat: HeartPulse,
  brain: Brain,
  breathe: Wind,
  sweat: Droplets,
  activity: Activity,

  // Timer & Time
  timer: Timer,
  clock: Clock,
  calendar: Calendar,
  calendarDays: CalendarDays,
  calendarCheck: CalendarCheck,

  // Actions
  refresh: RefreshCw,
  reset: RotateCcw,
  play: Play,
  pause: Pause,
  stop: Square,
  soundOn: Volume2,
  soundOff: VolumeX,

  // Concepts
  shield: Shield,
  idea: Lightbulb,
  explore: Compass,
  journey: Map,

  // Communication
  message: MessageCircle,
  send: Send,
  notify: Bell,
  alert: BellRing,

  // Stats
  barChart: BarChart3,
  lineChart: LineChart,
  pieChart: PieChart,
  up: ArrowUp,
  down: ArrowDown,
  trendUp: TrendingUp,
  trendDown: TrendingDown,

  // User
  user: User,
  users: Users,
  graduate: GraduationCap,

  // Journal & Writing
  write: Pencil,
  journal: FileText,
  book: BookMarked,

  // Activities
  exercise: Dumbbell,
  relax: Coffee,

  // Time of day
  night: Moon,
  day: Sun,
  morning: Sunrise,
} as const;

export type IconName = keyof typeof Icons;

interface IconProps {
  name: IconName;
  size?: number | string;
  className?: string;
  strokeWidth?: number;
  color?: string;
}

export function Icon({
  name,
  size = 20,
  className = '',
  strokeWidth = 2,
  color,
}: IconProps) {
  const IconComponent = Icons[name];
  return (
    <IconComponent
      size={size}
      className={className}
      strokeWidth={strokeWidth}
      color={color}
    />
  );
}

// Achievement icon type to Lucide component mapping
export const AchievementIconComponents: Record<string, typeof Trophy> = {
  'refresh': RefreshCw,
  'compass': Compass,
  'arrow-up': ArrowUp,
  'flame': Flame,
  'shield': Shield,
  'calendar': Calendar,
  'zap': Zap,
  'star': Star,
  'brain': Brain,
  'graduation-cap': GraduationCap,
  'trophy': Trophy,
  'crown': Crown,
};

// Achievement ID to icon name mapping (for reference)
export const AchievementIcons: Record<string, IconName> = {
  'first-strategy': 'refresh',
  'neural-explorer': 'explore',
  'level-up': 'up',
  'week-streak': 'streak',
  'two-week-warrior': 'shield',
  'monthly-master': 'calendar',
  'stress-shifter': 'stress',
  'stress-master': 'star',
  'brain-builder': 'brain',
  'synergy-scholar': 'graduate',
  'neural-champion': 'achievement',
  'grand-master': 'crown',
};

// Symptom icon mapping (replaces emojis)
export const SymptomIcons: Record<string, IconName> = {
  'racing-heart': 'heartbeat',
  'sweaty-palms': 'sweat',
  'butterflies': 'activity',
  'tight-chest': 'breathe',
  'shaky-hands': 'activity',
  'racing-thoughts': 'brain',
};

export { type LucideIcon };
